import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyTypography, BaseButton, useT } from '@repo/common';
import { ReactComponent as CheckIcon20 } from '@repo/common/assets/icons/24px/icon_24px_check.svg';
import { ReactComponent as ResetIcon } from '@repo/common/assets/icons/20px/icon_20px_reset.svg';
import { ReactComponent as NavigationLeftIcon } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { getPaymentByOrderId } from '../../api/payment/payment.api';

const PaymentSuccess = () => {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // StrictMode에서 상태 확인이 이중 실행되지 않도록 한다.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Eximbay 로 전체 페이지가 이동하면 결제 페이지의 언마운트 정리가 돌지 않아 예약 캐시가 남는다.
    // 결제가 거절된 뒤 같은 장바구니를 다시 결제하면 만료된 예약 ID 를 재사용해 실패하므로 여기서 비운다.
    try {
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('kpathport:bookings:'))
        .forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      // ignore
    }

    const processPayment = async () => {
      try {
        const orderId =
          searchParams.get('order_id') || searchParams.get('orderId');

        if (!orderId) {
          setError('결제 정보가 올바르지 않습니다.');
          setIsProcessing(false);
          return;
        }

        let paymentData;
        for (let attempt = 0; attempt < 12; attempt += 1) {
          paymentData = await getPaymentByOrderId(orderId);
          if (paymentData?.paymentStatus === 'DONE') break;
          if (paymentData?.paymentStatus === 'ABORTED') {
            throw new Error(
              paymentData?.cancelReason ||
                t('Payment was declined.', '결제가 거절되었습니다.')
            );
          }
          if (['CANCELED', 'EXPIRED'].includes(paymentData?.paymentStatus)) {
            throw new Error(
              t('Payment was not completed.', '결제가 완료되지 않았습니다.'),
            );
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        if (paymentData?.paymentStatus !== 'DONE') {
          throw new Error(
            t(
              'Payment confirmation is still pending.',
              '결제 확인이 아직 완료되지 않았습니다.',
            ),
          );
        }

        // step01 워크샵 예약인 경우 상태를 ongoing으로 변경
        if (paymentData?.bookingId) {
          // TODO: 실제로는 booking 정보를 가져와서 stepNumber를 확인해야 함
          // 일단은 결제 완료 시 step01 상태를 ongoing으로 설정
          // 실제 구현 시에는 booking의 workshop stepNumber를 확인하여 해당 step의 상태를 업데이트
          localStorage.setItem('step01_booking_status', 'ongoing');
        }

        // 결제 확인 성공 시 자동으로 Confirmed 페이지로 이동
        navigate(`/community/kpathport/confirmed?orderId=${orderId}`, {
          replace: true,
        });
      } catch (error) {
        console.error('Payment confirmation failed:', error);
        setError(
          error?.message ||
            t(
              'Payment confirmation failed. Please contact support.',
              '결제 확인에 실패했습니다. 고객센터로 문의해주세요.',
            )
        );
        setIsProcessing(false);
      }
    };

    processPayment();
  }, [searchParams, navigate]);

  const handleGoToConfirmed = () => {
    const orderId = searchParams.get('order_id') || searchParams.get('orderId');
    if (orderId) {
      navigate(`/community/kpathport/confirmed?orderId=${orderId}`);
    } else {
      navigate('/community/kpathport/confirmed');
    }
  };

  const handleGoToHome = () => {
    navigate('/community/kpathport/pathfeed');
  };

  if (isProcessing) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-grayscale-gray00'>
        <div className='text-center'>
          <p className={applyTypography('h5')}>결제를 확인하는 중입니다...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const handleRetry = () => {
      const orderId =
        searchParams.get('order_id') || searchParams.get('orderId');
      if (orderId) {
        navigate(`/community/kpathport/payments?orderId=${orderId}`);
      } else {
        navigate('/community/kpathport/booking');
      }
    };

    return (
      <div className='flex items-center justify-center min-h-screen bg-grayscale-gray00'>
        <div className='flex flex-col gap-[24px] items-center pb-[80px] px-[24px]'>
          <div className='flex flex-col gap-[2px] items-center text-center'>
            <p
              className={`${applyTypography('b20Strong')} text-text-primary`}
              style={{ lineHeight: '1.5' }}
            >
              {t('Payment not completed', '결제가 완료되지 않았습니다')}
            </p>
            <p
              className={`${applyTypography('b16')} text-text-primary`}
              style={{ lineHeight: '1.5' }}
            >
              {error}
            </p>
          </div>
          <div className='flex flex-col gap-[8px] items-center w-[180px]'>
            <BaseButton
              type='primary'
              size='l'
              htmlType='button'
              typo={false}
              gap='gap-[6px]'
              icon={
                <ResetIcon className='shrink-0 size-[20px] text-text-inverse' />
              }
              onClick={handleRetry}
              textWrapperClass={`${applyTypography(
                'b16Strong',
              )} h-auto flex-none text-text-inverse whitespace-nowrap leading-[1.5]`}
              className='h-[48px] min-w-0 border-0 pl-[12px] pr-[16px] py-[12px] rounded-[8px] w-full bg-green-deep02 hover:bg-green-deep03 transition-colors'
            >
              {t('Try again', '다시 시도')}
            </BaseButton>
            <BaseButton
              type='text'
              size='l'
              htmlType='button'
              typo={false}
              isStatic
              gap='gap-[6px]'
              icon={
                <div className='flex items-center overflow-clip p-[2px] shrink-0 w-[20px]'>
                  <NavigationLeftIcon className='w-full h-full text-grayscale-gray07' />
                </div>
              }
              onClick={() => navigate(-1)}
              textWrapperClass={`${applyTypography(
                'b16Strong',
              )} h-auto flex-none text-grayscale-gray07 whitespace-nowrap leading-[1.5]`}
              className='h-[48px] min-w-0 cursor-pointer border-0 bg-transparent pl-[12px] pr-[16px] py-[12px] rounded-[8px] w-full hover:opacity-70 transition-opacity'
            >
              {t('Go back', '뒤로 가기')}
            </BaseButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center justify-center min-h-screen bg-grayscale-gray00 p-8'>
      <div className='max-w-md w-full text-center'>
        <div className='bg-functional-success03 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6'>
          <CheckIcon20 className='w-12 h-12 text-grayscale-gray00' />
        </div>
        <h1 className={`${applyTypography('h4')} text-text-primary mb-4`}>
          결제가 완료되었습니다!
        </h1>
        <p className={`${applyTypography('b16')} text-text-placeholder mb-8`}>
          예약이 성공적으로 확정되었습니다.
        </p>
        <div className='flex flex-col gap-4'>
          <BaseButton onClick={handleGoToConfirmed} type='primary' size='l'>
            예약 확인하기
          </BaseButton>
          <BaseButton onClick={handleGoToHome} type='secondary' size='l'>
            홈으로 이동
          </BaseButton>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
